import { useEffect, useState } from "react";
import { OrganizationService, ApiError } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import SimpleSpinner from "../../loader";
import EmptyState from "../../../newComponents/emptystate";
import { GetImages } from "../../../utils/GetImages";
import { PubSub, TopicsEnum, NotificationType } from "../../../utils/pubsub";
import { updateTrialExpirationDate } from "./organizationSubscriptionApi";

// Mirrors client-api's generated SubscriptionTypesEnum (Trial = 1, PayAsYouGo = 2) without importing
// it directly, since `subscription?.subscriptionType` on the raw success payload here is read off an
// untyped `any` (see the component's existing convention of not typing the raw API response shapes).
const TRIAL_SUBSCRIPTION_TYPE = 1;

/**
 * Read-only subscription + billing view for a single organization, parameterized by the selected
 * org id (Platform portal). This is a fresh, un-permission-gated component rather than a reuse of
 * the org-user `MySubscriptionComponent`, which is hard-bound to the session org id and gated on
 * the "Subscription" permission (absent on a Platform token).
 */
const OrgSubscriptionTab = ({ organizationId }: { organizationId: number }) => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [currentInvoice, setCurrentInvoice] = useState<any>(null);
  const [pastInvoices, setPastInvoices] = useState<any[]>([]);
  const [isEditingExpiration, setIsEditingExpiration] = useState(false);
  const [expirationDraft, setExpirationDraft] = useState("");
  const [isSavingExpiration, setIsSavingExpiration] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const subRes = await apiHelper(
      OrganizationService.getApiOrganizationSubscriptionDetails,
      { showNotification: false },
      organizationId
    );
    setSubscription(subRes?.data?.success ?? null);

    try {
      const curRes = await OrganizationService.getApiOrganizationCurrentSubscriptionInvoice(organizationId);
      setCurrentInvoice(curRes?.success ?? null);
    } catch (err) {
      // A 404 is the expected "org has no current invoice yet" case — render the empty state.
      // Any other error is unexpected, so log it instead of silently swallowing it, then still
      // fall back to the empty state to keep the rest of the page usable.
      if ((err as ApiError)?.status !== 404) {
        console.error("Failed to load current subscription invoice:", err);
      }
      setCurrentInvoice(null);
    }

    const pastRes = await apiHelper(
      OrganizationService.getApiOrganizationPastSubscriptionInvoice,
      { showNotification: false },
      organizationId
    );
    const past = pastRes?.data?.success;
    setPastInvoices(Array.isArray(past) ? past : []);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const startEditingExpiration = () => {
    const currentValue = subscription?.expirationDate;
    setExpirationDraft(currentValue ? String(currentValue).slice(0, 10) : "");
    setIsEditingExpiration(true);
  };

  const cancelEditingExpiration = () => {
    setIsEditingExpiration(false);
    setExpirationDraft("");
  };

  const saveExpirationDate = async () => {
    if (!expirationDraft) {
      return;
    }
    setIsSavingExpiration(true);
    try {
      await updateTrialExpirationDate(organizationId, new Date(expirationDraft));
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Success,
        title: "Success",
        message: "Trial expiration date updated.",
      });
      setIsEditingExpiration(false);
      await fetchData();
    } catch (error) {
      console.error("Failed to update trial expiration date:", error);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        title: "Failure",
        message: "Unable to update the trial expiration date. Please try again.",
      });
    } finally {
      setIsSavingExpiration(false);
    }
  };

  if (loading) {
    return (
      <div className="platform-loadmore">
        <SimpleSpinner />
      </div>
    );
  }

  return (
    <div className="platform-tab-section">
      <div className="platform-info-card">
        <div className="platform-overview-grid">
          <div className="platform-overview-item">
            <label>Subscription Type</label>
            <p className="normal_text black m-0">
              {subscription?.formatted?.SubscriptionType || "—"}
            </p>
          </div>
          <div className="platform-overview-item">
            <label>Expires On</label>
            {isEditingExpiration ? (
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <input
                  type="date"
                  className="form-control"
                  value={expirationDraft}
                  onChange={(e) => setExpirationDraft(e.target.value)}
                  disabled={isSavingExpiration}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={saveExpirationDate}
                  disabled={isSavingExpiration || !expirationDraft}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={cancelEditingExpiration}
                  disabled={isSavingExpiration}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <p className="normal_text black m-0">
                {subscription?.formatted?.ExpirationDateDateString || "—"}
                {subscription?.subscriptionType === TRIAL_SUBSCRIPTION_TYPE && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-2"
                    onClick={startEditingExpiration}
                  >
                    Edit
                  </button>
                )}
              </p>
            )}
          </div>
          <div className="platform-overview-item">
            <label>Billing Cycle</label>
            <p className="normal_text black m-0">
              {subscription?.formatted?.BillingCycle || "—"}
            </p>
          </div>
          <div className="platform-overview-item">
            <label>Auto-Renewal</label>
            <p className="normal_text black m-0">{subscription?.autoRenewal ? "On" : "Off"}</p>
          </div>
        </div>
      </div>

      <div className="platform-info-card">
        <h5 className="section-title black">Current Invoice</h5>
        {currentInvoice ? (
          <div className="platform-overview-grid">
            <div className="platform-overview-item">
              <label>Amount Due</label>
              <p className="normal_text black m-0">{currentInvoice?.formatted?.Amount || "—"}</p>
            </div>
            <div className="platform-overview-item">
              <label>Due Date</label>
              <p className="normal_text black m-0">
                {currentInvoice?.formatted?.DueDateDateString || "—"}
              </p>
            </div>
            <div className="platform-overview-item">
              <label>Invoice Number</label>
              <p className="normal_text black m-0"># {currentInvoice?.referenceNumber || "—"}</p>
            </div>
            <div className="platform-overview-item">
              <label>Status</label>
              <p className="normal_text black m-0">
                {currentInvoice?.formatted?.PaymentDueAccountStatusId || "—"}
              </p>
            </div>
          </div>
        ) : (
          <EmptyState
            heading="No Active Invoice"
            icon={GetImages.emptyStateData}
            isButton={false}
            isBorder={false}
          />
        )}
      </div>

      <div className="platform-info-card">
        <h5 className="section-title black">Billing History</h5>
        {pastInvoices.length > 0 ? (
          <div className="platform-table-wrapper">
            <table className="platform-table">
              <thead>
                <tr>
                  <th>Invoice No.</th>
                  <th>Billing Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pastInvoices.map((item, idx) => (
                  <tr key={idx}>
                    <td># {item?.referenceNumber || "—"}</td>
                    <td>{item?.formatted?.DueDateDateString || "—"}</td>
                    <td>{item?.formatted?.Amount || "—"}</td>
                    <td>{item?.formatted?.PaymentDueAccountStatusId || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            heading="No past invoices"
            icon={GetImages.emptyStateData}
            isButton={false}
            isBorder={false}
          />
        )}
      </div>
    </div>
  );
};

export default OrgSubscriptionTab;
